import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../../utils/api';
import config from '../../../config';
import i18n from '../../../i18n';
import {
  fetchEntryExitReasons,
  fetchEntryExitReasonsSuccess,
  fetchEntryExitReasonsFailure,
  fetchEntryExitReasonById,
  fetchEntryExitReasonByIdSuccess,
  fetchEntryExitReasonByIdFailure,
  addEntryExitReason,
  addEntryExitReasonSuccess,
  addEntryExitReasonFailure,
  updateEntryExitReason,
  updateEntryExitReasonSuccess,
  updateEntryExitReasonFailure,
  deleteEntryExitReason,
  deleteEntryExitReasonSuccess,
  deleteEntryExitReasonFailure,
  bulkDeleteEntryExitReasons,
  bulkDeleteEntryExitReasonsSuccess,
  bulkDeleteEntryExitReasonsFailure,
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
} from './slice';
import entryExitReasonService from '../../../services/entryExitReasonService';

/**
 * Translate error message from backend
 * Maps common backend error messages to i18n translation keys
 */
function translateError(errorMessage) {
  if (!errorMessage) return i18n.t('entryExitReasons.errors.fetchFailed');

  // Common error message mappings
  const errorMap = {
    'Entry/Exit Reason already exists': 'entryExitReasons.errors.reasonExists',
    'Entry/Exit Reason not found': 'entryExitReasons.errors.reasonNotFound',
    'Failed to add entry/exit reason': 'entryExitReasons.errors.createFailed',
    'Failed to update entry/exit reason': 'entryExitReasons.errors.updateFailed',
    'Failed to delete entry/exit reason': 'entryExitReasons.errors.deleteFailed',
    'Failed to fetch entry/exit reasons': 'entryExitReasons.errors.fetchFailed',
  };

  // Check if error message matches a known pattern
  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  // If no mapping found, return original message
  return errorMessage;
}

// Worker saga: Fetch init data (entry exit statuses)
function* handleFetchInit() {
  try {
    // Fetch initialization data from init endpoint
    const response = yield call(entryExitReasonService.getInit);
    yield put(fetchInitSuccess({ entryExitStatuses: response.entryExitStatuses || [] }));
  } catch (error) {
    yield put(fetchInitFailure(error.message));
  }
}

// Worker saga: Fetch entry/exit reasons
function* handleFetchEntryExitReasons(action) {
  try {
    // Get page, pageSize, sortColumn, sortDirection, filterName, and filterStatusId from action payload (if provided)
    const { page, pageSize, sortColumn, sortDirection, filterName, filterStatusId } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      params.sortColumn = sortColumn;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterName) {
      params.search = filterName;
    }
    if (filterStatusId !== undefined && filterStatusId !== null) {
      params.statusId = filterStatusId;
    }

    const response = yield call(api.get, config.endpoints.entryExitReasons.list, params);
    // Pass the whole response (including pagination info)
    yield put(fetchEntryExitReasonsSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchEntryExitReasonsFailure(translatedError));
  }
}

// Worker saga: Fetch entry/exit reason by ID
function* handleFetchEntryExitReasonById(action) {
  try {
    const entryExitReasonId = action.payload;
    const response = yield call(api.get, `${config.endpoints.entryExitReasons.list}/${entryExitReasonId}`);
    yield put(fetchEntryExitReasonByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchEntryExitReasonByIdFailure(translatedError));
  }
}

// Worker saga: Add entry/exit reason
function* handleAddEntryExitReason(action) {
  try {
    const response = yield call(api.post, config.endpoints.entryExitReasons.create, action.payload);
    yield put(addEntryExitReasonSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(addEntryExitReasonFailure(translatedError));
  }
}

// Worker saga: Update entry/exit reason
function* handleUpdateEntryExitReason(action) {
  try {
    const { id } = action.payload;
    // Include id in the request body
    const response = yield call(api.put, `${config.endpoints.entryExitReasons.update}/${id}`, action.payload);
    yield put(updateEntryExitReasonSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(updateEntryExitReasonFailure(translatedError));
  }
}

// Worker saga: Delete entry/exit reason
function* handleDeleteEntryExitReason(action) {
  try {
    const entryExitReasonId = action.payload;
    yield call(api.delete, `${config.endpoints.entryExitReasons.delete}/${entryExitReasonId}`);
    yield put(deleteEntryExitReasonSuccess(entryExitReasonId));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(deleteEntryExitReasonFailure(translatedError));
  }
}

// Worker saga: Bulk delete entry/exit reasons
function* handleBulkDeleteEntryExitReasons(action) {
  try {
    const entryExitReasonIds = action.payload;
    // Call API DELETE /entry-exit-reasons with body: [1, 2, 3, ...]
    yield call(api.delete, config.endpoints.entryExitReasons.delete, { data: entryExitReasonIds });
    yield put(bulkDeleteEntryExitReasonsSuccess(entryExitReasonIds));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(bulkDeleteEntryExitReasonsFailure(translatedError));
  }
}

// Watcher saga
export function* watchEntryExitReasonsActions() {
  yield takeLatest(fetchInit.type, handleFetchInit);
  yield takeLatest(fetchEntryExitReasons.type, handleFetchEntryExitReasons);
  yield takeLatest(fetchEntryExitReasonById.type, handleFetchEntryExitReasonById);
  yield takeLatest(addEntryExitReason.type, handleAddEntryExitReason);
  yield takeLatest(updateEntryExitReason.type, handleUpdateEntryExitReason);
  yield takeLatest(deleteEntryExitReason.type, handleDeleteEntryExitReason);
  yield takeLatest(bulkDeleteEntryExitReasons.type, handleBulkDeleteEntryExitReasons);
}
